import { useEffect } from "react";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { NAP, IMAGES } from "@/data/site";
import { Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { track } from "@/lib/api";
import { ChatBot } from "@/components/ChatBot";
import { useSEO, localBusinessLd, breadcrumbLd } from "@/lib/seo";

// Full-page "Ask MRE": the site assistant rendered inline, open on arrival.

export default function Chat() {
  useSEO({
    title: "Ask MRE — AI Assistant | MRE Developments Calgary",
    description:
      "Ask the MRE Developments assistant about renovations, custom homes, commercial build-outs, service areas and timelines. Answers in seconds, any hour.",
    path: "/chat",
    image: IMAGES.ai,
    jsonLd: [
      localBusinessLd(),
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "Ask MRE", path: "/chat" }]),
    ],
  });

  useEffect(() => {
    track("chat_page_view", { location: "chat" });
  }, []);

  return (
    <div data-testid="chat-page">
      <PageHero
        overline="Ask MRE"
        title="Ask us anything. Right now."
        intro="Our assistant answers questions about renovations, custom homes, commercial build-outs, service areas and timelines — any hour of the day. Prefer a human? Call or email and you'll hear back within one business day."
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Ask MRE", path: "/chat" }]}
      />

      <section className="max-w-[1100px] mx-auto px-5 sm:px-8 py-12 lg:py-16">
        <div data-testid="chat-embed">
          <ChatBot inline />
        </div>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <a
            href={`tel:${NAP.phoneHref}`}
            onClick={() => track("click_to_call", { location: "chat" })}
            data-testid="chat-call"
            className="flex items-center gap-4 border border-border p-5 bg-secondary/30 hover:border-primary transition-colors"
          >
            <Phone size={22} className="text-primary shrink-0" />
            <div>
              <div className="font-display uppercase tracking-tight text-lg">Talk to a person</div>
              <div className="text-muted-foreground text-sm mt-0.5">{NAP.phoneDisplay}</div>
            </div>
          </a>
          <a
            href={`mailto:${NAP.email}`}
            onClick={() => track("click_to_email", { location: "chat" })}
            data-testid="chat-email"
            className="flex items-center gap-4 border border-border p-5 bg-secondary/30 hover:border-primary transition-colors"
          >
            <EnvelopeSimple size={22} className="text-primary shrink-0" />
            <div>
              <div className="font-display uppercase tracking-tight text-lg">Send details</div>
              <div className="text-muted-foreground text-sm mt-0.5">{NAP.email}</div>
            </div>
          </a>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
